import { QueryClient, QueryFunction } from '@tanstack/react-query';
import { createHttpClient } from '@infra/http';
import type { HttpError } from '@infra/http';
import { resolveTimeZone } from '@infra/format';
import { getCurrentUser } from '@services/storage';
import type { AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { environment } from '@shared/config';
import {
  getAccessToken as getCoreAccessToken,
  readStoredTokens,
  getOrStartRefresh,
  startTokenRefreshScheduler,
  sessionRevokedNotifier,
  sessionEndReasonFromCode,
  decodeJwtPayload,
} from '@infra/auth';
import { ACCESS_STALE_EVENT } from '../../access/access.events';
import {
  ME_ACCESS_ENDPOINT,
  ORG_SUSPENDED_CODE,
  ROLE_NOT_ALLOWED_CODE,
} from '../../access/access.types';

type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';

export const coreHttp = createHttpClient({
  baseURL: environment.apiUrl,
  timeout: environment.apiTimeout,
  getAccessToken: () => getCoreAccessToken(),
});

// ──────────────────────────────────────────────
// Timezone: sent on every request, not per endpoint
// ──────────────────────────────────────────────

/**
 * The zone this client renders dates in, so the server cuts day buckets on the
 * boundary the labels will show.
 *
 * Global rather than a parameter on the endpoints that need it: omitting it fails
 * silently — server buckets at UTC, UI labels in local time, early-morning
 * sessions land a day early — and no type checks for a missing argument.
 */
coreHttp.interceptors.request.use((config) => {
  config.headers.set('X-Timezone', resolveTimeZone(getCurrentUser()?.timezone));
  return config;
});

// ──────────────────────────────────────────────
// 401 interceptor: auto-refresh, else session-ended modal
// ──────────────────────────────────────────────

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

coreHttp.interceptors.response.use(
  undefined, // don't intercept success — already handled upstream
  async (error: HttpError) => {
    // A session that ended server-side must not refresh (it would 401 again) — show the
    // modal. Covers both the single-session kick and an admin deactivation.
    const endedReason = sessionEndReasonFromCode(
      (error.data as { code?: string } | undefined)?.code
    );
    if (error.status === 401 && endedReason) {
      sessionRevokedNotifier.notify(endedReason);
      return Promise.reject(error);
    }

    // A 403/404 may mean the org role changed. Skip the access call itself so it cannot loop.
    const staleUrl = (error._requestConfig as { url?: string } | undefined)?.url ?? '';
    // A coded refusal is a business rule; only a role refusal or a suspension says the access record moved.
    const refusalCode = (error.data as { code?: unknown } | undefined)?.code;
    const accessMayHaveChanged =
      typeof refusalCode !== 'string' ||
      refusalCode === ROLE_NOT_ALLOWED_CODE ||
      refusalCode === ORG_SUSPENDED_CODE;
    if (
      (error.status === 403 || error.status === 404) &&
      accessMayHaveChanged &&
      !staleUrl.includes(ME_ACCESS_ENDPOINT) &&
      typeof window !== 'undefined'
    ) {
      window.dispatchEvent(new Event(ACCESS_STALE_EVENT));
    }

    // The error has been normalized by http.ts. Original config is in _requestConfig.
    const originalRequest = error._requestConfig as RetryableRequestConfig | undefined;

    // Only handle 401, skip if already retried or is an auth endpoint
    if (
      error.status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      originalRequest.url?.includes('/auth/')
    ) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const tokens = readStoredTokens();
      if (!tokens?.refresh_token) {
        throw new Error('No refresh token available');
      }

      // getOrStartRefresh persists the rotated tokens (inside the cross-tab lock).
      const newTokens = await getOrStartRefresh(tokens.refresh_token);
      startTokenRefreshScheduler();

      // R7: if a sibling tab logged in as a DIFFERENT user mid-refresh (runRefresh's
      // identity bail returns that user's tokens), don't replay THIS request — built for
      // the old identity — with the new user's bearer. Reject; the app re-issues under the
      // new session. Compare the refreshed token's sub against the request's old bearer.
      const oldBearer = (originalRequest.headers.Authorization as string | undefined)?.replace(
        /^Bearer\s+/i,
        ''
      );
      const oldSub = decodeJwtPayload(oldBearer ?? undefined)?.sub;
      const newSub = decodeJwtPayload(newTokens.access_token)?.sub;
      if (oldSub && newSub && oldSub !== newSub) {
        return Promise.reject(error);
      }

      if (newTokens.access_token) {
        originalRequest.headers.Authorization = `Bearer ${newTokens.access_token}`;
      }
      return coreHttp.request(originalRequest);
    } catch (refreshError) {
      // Only a DEFINITIVE failure (dead/missing refresh token) means the session
      // is really over → surface the SessionRevokedGuard modal (deferred + non-
      // abrupt, so an active roleplay voice/avatar WS isn't killed mid-turn).
      // A TRANSIENT failure (network blip / 5xx) must NOT sign the user out: just
      // reject this request and let a later one — or the proactive scheduler —
      // retry once connectivity returns.
      const status = (refreshError as { response?: { status?: number } })?.response?.status;
      // A superseded session (single-session kick) can now surface from /auth/refresh
      // itself (401 SESSION_SUPERSEDED). Route it to the 'superseded' modal, distinct
      // from 'expired', purely for copy — both now end the FusionAuth SSO session via
      // endSession()/LogoutCallback, so which reason is picked no longer risks a
      // silent re-auth ping-pong either way.
      const code = (refreshError as { response?: { data?: { code?: string } } })?.response?.data
        ?.code;
      const refreshTokenGone = !readStoredTokens()?.refresh_token;
      if (refreshTokenGone || status === 400 || status === 401) {
        // Not a known session-ended code → the refresh token itself is dead.
        sessionRevokedNotifier.notify(sessionEndReasonFromCode(code) ?? 'expired');
      }
      return Promise.reject(error);
    }
  }
);

// ──────────────────────────────────────────────

export async function apiRequest<T = unknown>(
  method: HttpMethod,
  url: string,
  data?: unknown | undefined,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await coreHttp.request<T>({
    method,
    url,
    data,
    ...config,
  });

  return response.data;
}

type UnauthorizedBehavior = 'returnNull' | 'throw';
export const getQueryFn =
  <T>(options: { on401: UnauthorizedBehavior }): QueryFunction<T> =>
  async ({ queryKey }) => {
    try {
      const response = await coreHttp.get<T>(queryKey.join('/') as string);
      return response.data;
    } catch (error) {
      const httpError = error as HttpError;
      if (options.on401 === 'returnNull' && httpError.status === 401) {
        return null as T;
      }
      throw error;
    }
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: 'throw' }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
